import React from "react";
import { BigNumber, limitDecimals } from "../../../global/utils";
import { ContractsManagerContext, SystemSummaryContext, FunctionsContext, WalletContext, StrategyContext } from "../../../global/contexts";
import { ISysAccountInfoObj, ISysPooledFundInfoObj } from "../../../global/types";
import { ethers } from "ethers";

export function StrategyFundsSidebarElement() {
  const { contractManager } = React.useContext(ContractsManagerContext);
  const { selectedStrategy } = React.useContext(StrategyContext);
  const { SystemSummary } = React.useContext(SystemSummaryContext);
  const { wallet } = React.useContext(WalletContext);
  const { tryTx, toggleDepositWithdrawDialog, showErrorMessage } = React.useContext(FunctionsContext);

  const [current, setCurrent] = React.useState(0);

  React.useEffect(() => {
    let _current = 0;

    if (selectedStrategy.isPooledFund && "memberBalance" in SystemSummary.currentContract) {
      _current = (SystemSummary.currentContract as ISysPooledFundInfoObj).memberBalance.current;
    }
    else {
      _current = (SystemSummary.currentContract as ISysAccountInfoObj).contractBalance.current.toNumber();
    }

    setCurrent(limitDecimals(_current / contractManager.cash.denominator.toNumber()));
  }, [SystemSummary.currentContract, selectedStrategy.isPooledFund, contractManager.cash.denominator]);

  async function joinStrategy() {
    if (!wallet.provider) {
      throw new Error("No wallet provider in joinStrategy()!");
    }

    if (!contractManager.contracts.Controller.functions) {
      throw new Error("No Controller functions in joinStrategy()!");
    }

    const balanceAVAX = await wallet.provider.getBalance(wallet.address);
    const reqAVAX: ethers.BigNumber = await contractManager.contracts.Controller.functions.avaxReqToJoinStrategy();
    const additionalAVAX = BigNumber(2).mul(BigNumber(10).pow(18)).div(10);

    if (balanceAVAX.lt(reqAVAX.add(additionalAVAX))) {
      const balanceAVAX_str = ethers.utils.formatEther(balanceAVAX);
      const reqAVAX_str = ethers.utils.formatEther(reqAVAX.add(additionalAVAX));

      showErrorMessage({
        message: `JOIN STRATEGY: Insufficient AVAX to join strategy. 
        Have: ${limitDecimals(Number(balanceAVAX_str), 4)} AVAX. 
        Required: ${limitDecimals(Number(reqAVAX_str), 4)} AVAX.`
      });
      return;
    }

    tryTx(contractManager.contracts.Controller.functions.joinStrategy, [SystemSummary.AddressBook.accountFactory], reqAVAX.toString());
  }

  function getButton() {
    if (!SystemSummary.isRegistered && !selectedStrategy.isPooledFund) {
      return (
        // Maybe do some css hover shenanigans here
        <button
          className="etx-button Button border-radius-0"
          onClick={joinStrategy}
          style={{ background: "rgb(190, 120, 0)" }}
        >
          <span className="etx-button__text Button__text">Join Strategy</span>
        </button>
      );
    }
    else {
      return (
        <>
          <button
            className="etx-button Button depositColour border-radius-0"
            onClick={() => toggleDepositWithdrawDialog("DEPOSIT")}
          >
            <span className="etx-button__text Button__text">Deposit</span>
          </button>
          <button
            className="etx-button Button withdrawColour border-radius-0"
            onClick={() => toggleDepositWithdrawDialog("WITHDRAW")}
          >
            <span className="etx-button__text Button__text">Withdraw</span>
          </button>
        </>
      );
    }
  }

  return (
    <div className="etx-card Card bg-white-1-20 border-radius overflow-hidden">
      <div className="etx-card__item Card__item pv-50 bg-white-1-20">
        <div className="etx-flex Flex c-auto av-center gh-50 gv-0">
          <div className="etx-flex__item Flex__item grow-1">
            <div className="etx-text Text a-center">
              <h5>My&nbsp;&nbsp;Account&nbsp;&nbsp;Funds</h5>
            </div>
          </div>
        </div>
      </div>

      <div className="etx-card__item Card__item pv-50">
        <div className="etx-flex Flex c-auto av-center gh-50 gv-0">
          <div
            className="etx-flex__item Flex__item shrink-0"
          >
            <div className="etx-text Text">
              <p className="font-label">
                {contractManager.cash.name}
              </p>
            </div>
          </div>

          <div className="etx-flex__item Flex__item grow-1">
            <div className="etx-text Text a-right">
              <p className="font-size-large font-weight-medium">
                {current}
              </p>
            </div>
          </div>
        </div>
      </div>

      {
        SystemSummary.currentContract.tradingEngineInfo.pending.withdrawAmountCash.gt(0)
          ? <div className="etx-card__item Card__item ph-0 pv-0">
            <hr />
          </div>
          : ""
      }

      {
        SystemSummary.currentContract.tradingEngineInfo.pending.withdrawAmountCash.gt(0) ? (
          <div className="etx-card__item Card__item pv-50">
            <div className="etx-flex Flex c-auto av-center gh-50 gv-0">
              <div
                className="etx-flex__item Flex__item shrink-0"
                title="Requested amount of cash that you'd like to receive when the current trade exits."
              >
                <div className="etx-text Text">
                  <p className="font-label">
                    {contractManager.cash.name} Pending Withdrawal<i className="far fa-info-circle"></i>
                  </p>
                </div>
              </div>

              <div className="etx-flex__item Flex__item grow-1">
                <div className="etx-text Text a-right">
                  <p className="font-size-large font-weight-medium">
                    {limitDecimals(SystemSummary.currentContract.tradingEngineInfo.pending.withdrawAmountCash.toNumber() / contractManager.cash.denominator.toNumber())}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : ""
      }

      <div className="etx-card__item Card__item ph-0 pv-0">
        <hr />
      </div>

      <div className="etx-card__item Card__item pv-50">
        <div className="etx-flex Flex c-auto av-center gh-50 gv-0">
          <div
            className="etx-flex__item Flex__item shrink-0"
            title="Pooled across all strategies."
          >
            <div className="etx-text Text">
              <p className="font-label">
                <span style={{ fontSize: "0.95em" }}>Ŧ</span>E <i className="far fa-info-circle"></i>
              </p>
            </div>
          </div>

          <div className="etx-flex__item Flex__item grow-1">
            <div className="etx-text Text a-right">
              <p className="font-size-large font-weight-medium">
                {limitDecimals(SystemSummary.Controller.ownerInfoCurrent.balanceTE / contractManager.contracts.TE.denominator.toNumber())}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="etx-card__item Card__item ph-0 pv-0">
        <div className="etx-button-list ButtonList combo">
          {getButton()}
        </div>
      </div>
    </div>
  );
}
